/* eslint-disable @next/next/no-img-element */

import React, { useState, useEffect, Fragment, useRef, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import Backend from '../../../../utils/Backend';
import slugify from 'react-slugify';
import fetchColors from '../../../../helper/fetch_colors';
import { formatOptionPrice, formatRange, formatTrunk, formatCurrency, formatPrice } from '../../../../helper/vehicle_formatter';
import { vehicleTypes } from '../../../../helper/constants';
import { event } from '../../../../lib/gtag';

import Loader from '../../../Loader';
import ColorsGrid from '../../../ColorsGrid';
import PoweredByText from '../../../PoweredBy';

import IOption from '../../../../interfaces/IOption';

import styles from './styles.module.scss';
import BulletPoint from '../../../../public/assets/icons/bullet-point.svg';
import DropdownArrow from '../../../../public/assets/icons/dropdown-arrow.svg';
import DropdownArrowBoi from '../../../../public/assets/nevo-boi/chevron-up.svg';
import { useIsBoiWebsite } from "../../../Context/WebsiteType";

interface IOptionCard {
  option: any;
  financePage?: boolean;
  isEligableFinance?: boolean;
  commercial?: boolean;
}

const OptionCard = ({ option, financePage, commercial, isEligableFinance } : IOptionCard) => {
  const [imageURL, setImageURL] = useState("")
  
  const [colors, setColors] = useState<IOption[]>([])

  const [showMore, setShowMore] = useState(false)
  const [animation, setAnimation] = useState(false) 

  const detailsRef = useRef<HTMLDivElement>(null)
  const colorsRef= useRef<HTMLDivElement>(null)

  const router = useRouter()

  const isBoiWebsite = useIsBoiWebsite();

  // get image
  useEffect(() => {
    setImageURL(Backend.getImage(option, "21"))
  }, [option])

  // get colors
  useEffect(() => {
    const success = (colors: IOption[]) => {
      setColors(colors)
    }

    fetchColors(option, success, () => {})
  }, [option])


  useEffect(() => {
    if (animation) {

      const newShow = !showMore

      if (detailsRef.current) {
        detailsRef.current.style.maxHeight = newShow ? "450px" : "121px"
      }
      if (colorsRef.current) {
        colorsRef.current.style.maxHeight = newShow ? "225px" : "0"
      }

      if (newShow) {
        setAnimation(false)
        setShowMore(true)
      }
      else {
        // wait until it's hidden and then show less items
        setTimeout(() => {
          setAnimation(false)
          setShowMore(newShow)  
        }, 290)
      }
    }
  }, [showMore, animation, option])

  const onShowMore = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    setAnimation(true)
  }

  const renderPoint = (content: string, bold = false) => {
    return (
      <div className={`${isBoiWebsite ? styles.detailBoi : styles.detail}`}>
        <img src={BulletPoint.src} alt='bullet point'/>
        <p className={bold ? styles.bold : ""}>{ content }</p>
      </div>
    )
  }

  const {
    make, model, trim_level, body_type,
    fuel_type, price, range, 
    battery_kwh, maximum_power_hp,
    acceleration, seating_capacity,
    is_fast_charge,
    trunk_capacity, towing_capacity,
    // commercial properties
    vehicle_type, cargo_weight,
    cargo_capacity
  } = option

  const renderPoints = () => {
    let pointsToRender = []

   // rendering price
   if (financePage) {
    let finance_price = 0;

    if (option.finance_data?.monthly_cost) {
      finance_price = option.finance_data.monthly_cost
    }

    let text = `Personal Contract Plan (PCP) ${formatCurrency(finance_price)} per Month*`

    if (!finance_price) {
      text = "Personal Contract Plan (PCP) not available"
    }

    pointsToRender.push(renderPoint(text, true))
  }

  pointsToRender.push(renderPoint(formatOptionPrice(price), true))

    if (range > 0) {
      pointsToRender.push(renderPoint(`Up to ${range}km Range ${formatRange(fuel_type)}`))
    }
    if (battery_kwh > 0) {
      pointsToRender.push(renderPoint(`${battery_kwh}kWh Battery`))
    }
    if (maximum_power_hp > 0) {
      pointsToRender.push(renderPoint(`${maximum_power_hp}HP`))
    }
    if (acceleration > 0) {
      pointsToRender.push(renderPoint(`0-100km/h in ${acceleration} seconds`))
    }

    if (vehicle_type === vehicleTypes.commercial) {
      if (cargo_capacity > 0) {
        pointsToRender.push(renderPoint(`${cargo_capacity}m³ Load Capacity`))
      }
      if (cargo_weight > 0) {
        pointsToRender.push(renderPoint(`${cargo_weight}kg Load Weight`))
      }
    }

    pointsToRender.push(renderPoint(`${towing_capacity}kg Towing Capacity`))
    
    if (trunk_capacity > 0) {
      pointsToRender.push(renderPoint(`Trunk Capacity ${formatTrunk(trunk_capacity, body_type)}`))
    }
    if (seating_capacity > 0) {
      pointsToRender.push(renderPoint(`Seating Capacity ${seating_capacity}`))
    }

    // if fast charge
    if (is_fast_charge === 1) {
      pointsToRender.push(renderPoint("Fast Charge"))
    }

    if (!showMore) {
      pointsToRender = pointsToRender.slice(0, 4) // only show 4 points
    }

    return pointsToRender.map((point, index) => {
      return (
        <Fragment key={index}>{ point }</Fragment>
      )
    })
  }

  const openBaseOption = () => {
    if (option && router.query) {
      const { make, model } = router.query;
      
      router.push(`/vehicle/option/${make}/${model}/${option.third_party.variantId}`)
    }
  }

  const financeVehicle = useCallback((evt: any) => {
    evt.preventDefault()
    evt.stopPropagation()

    if (option && router.query) {
      event(financePage ? "financeVehicleOnFinanceClick" : "financeVehicleClick")

      const { make, model } = router.query;
      router.push(`/finance-vehicle/option/${make}/${model}/${option.third_party.variantId}`)
    }
  }, [option, router, financePage])

  /*  For non BOI brands in the full vehicle ‘on the road’ flow, a new ‘Finance Vehicle” button will be added.
      This will be a simple link to the BOI Car Loans site https://personalbanking.bankofireland.com/borrow/loans/car-loan/ 
      This will open in a new browser tab
  */

  const financeVehicleWithBOI = useCallback(() => {
    event('financeVehicleForBOIClick')
    window.open('https://personalbanking.bankofireland.com/borrow/loans/car-loan/', '_blank')
  }, [])

  const renderedFinanceButton = useMemo(() => {
    if (isEligableFinance) return (
      <section className={`${isBoiWebsite ? styles.leaseActionBoi : styles.leaseAction}`}>
        <button onClick={financeVehicle} className={`${isBoiWebsite ? `${styles.blueButtonBoi} dark-blue-button-boi` : `${styles.blueButton} blue-button`}`}>
          Finance Vehicle
        </button>

        <PoweredByText 
          text='Bank of Ireland' 
          textClassName={`${isBoiWebsite ? styles.bankOfIrelandTextBoi : styles.bankOfIrelandText}`}
        />
      </section>
    )
  }, [isEligableFinance, financeVehicle, isBoiWebsite])
  
  const renderedFinanceVehicleWithBOIButton = useMemo(() => {
    if (!financePage && !isEligableFinance) return (
        <section className={`${isBoiWebsite ? styles.leaseActionBoi : styles.leaseAction}`}>
          <button onClick={financeVehicleWithBOI} className={`${isBoiWebsite ? `${styles.blueButtonBoi} dark-blue-button-boi` : `${styles.blueButton} blue-button`}`}>
            Finance Vehicle
          </button>
  
        </section>
      )
  }, [financePage, financeVehicleWithBOI, isEligableFinance, isBoiWebsite])

  const base_url = useMemo(() => {
    return financePage ? '/finance-vehicle' : '/vehicle'
  }, [financePage])

  return (
    <Link href={`${base_url}/option/${slugify(make)}/${slugify(model)}/${option.third_party.variantId}`} passHref>
      <div className={`${isBoiWebsite ? styles.optionCardBoi : styles.optionCard}`}>
        
        <div className={`${isBoiWebsite ? styles.cardBoi : styles.card}`}>
          <div className={`${isBoiWebsite ? styles.infoHolderBoi : styles.infoHolder}`}>
            <p className={`${isBoiWebsite ? styles.fuelTypeBoi : styles.fuelType}`}>
              {
                fuel_type === "BEV" ? "All-Electric" :
                fuel_type === "PHEV" ? "Plug-in Hybrid" : fuel_type
              }
            </p>

            <div className={`${isBoiWebsite ? styles.trimLevelHolderBoi : styles.trimLevelHolder}`}>
              <h1 className={`${isBoiWebsite ? styles.trimLevelBoi : styles.trimLevel}`}>{ trim_level }</h1>

              <div className={`${isBoiWebsite ? styles.bodyTypeHolderBoi : styles.bodyTypeHolder}`}>
                <p className={`${isBoiWebsite ? styles.bodyTypeBoi : styles.bodyType}`}>{ body_type }</p>
              </div>
            </div>
            

            <div ref={detailsRef} className={`${isBoiWebsite ? styles.detailsHolderBoi : styles.detailsHolder}`}>
              { renderPoints() }
            </div>

            <div onClick={onShowMore} className={`${isBoiWebsite ? styles.showMoreBoi : styles.showMore}`}>
              <div className={`${isBoiWebsite ? styles.arrowHolderBoi : styles.arrowHolder}`}>
                <img src={isBoiWebsite ? DropdownArrowBoi.src : DropdownArrow.src} alt="arrow" style={!showMore ? { transform: "rotate(180deg)" } : {}} />
              </div>

              <p>Show { showMore ? "less" : "more" }</p>
            </div>
          </div>

          <div className={`${isBoiWebsite ? styles.imageColorsBoi : styles.imageColors}`}>
            {
              imageURL === ""
              ? <div className={`${isBoiWebsite ? styles.loaderHolderBoi : styles.loaderHolder}`}><Loader /></div> 
              : 
              <>
                <div className={`${isBoiWebsite ? styles.optionImageBoi : styles.optionImage}`} style={{ backgroundImage: `url(${imageURL})` }}>
                  &nbsp;
                </div>
                { (colors.length > 0) && (
                  <div ref={colorsRef} className={`${isBoiWebsite ? styles.colorsHolderBoi : styles.colorsHolder}`}>
                    <ColorsGrid colors={colors} />
                  </div>  
                )}
              </>
            }
          </div>
        </div>

        
        <div className={`
          ${isBoiWebsite ? styles.cardActionsBoi : styles.cardActions}  
          ${isEligableFinance && (!isBoiWebsite ? styles.hasLease : "")} 
        `}>
          {(!financePage) && (
            <button onClick={openBaseOption} className={`${isBoiWebsite ? `${styles.blueButtonBoi} dark-blue-button-boi` : `${styles.blueButton} blue-button`}`}>
              Test Drive <span>/</span> Enquire
            </button>
          )}

          { isEligableFinance ? renderedFinanceButton : isBoiWebsite ? renderedFinanceVehicleWithBOIButton : null }
        </div>
      </div>
    </Link>
  )
};

export default OptionCard;
